import { z } from "zod";
import { actorIdentity, actorIdentitySchema } from "./authority.js";
import { completionIntentSchema, parseRequiredCompletionIntent, } from "./completion.js";
import { UNIVERSAL_VERIFICATION_PROTOCOL, consequenceClassSchema, protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, sha256DigestSchema, uriSchema, } from "./identifiers.js";
export const universalWorkItemSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    id: protocolIdSchema,
    tenantId: protocolIdSchema,
    projectId: protocolIdSchema.nullable(),
    workType: protocolIdSchema,
    title: z.string().trim().min(1),
    description: z.string().trim().min(1),
    completionIntent: completionIntentSchema.optional(),
    state: z.enum([
        "REQUESTED",
        "CONTRACT_PENDING",
        "OPEN",
        "IN_PROGRESS",
        "SUBMITTED",
        "VERIFYING",
        "DETERMINED",
        "ENFORCED",
        "REVIEWING",
        "CLOSED",
    ]),
    consequence: consequenceClassSchema,
    requestedBy: actorIdentitySchema,
    source: z
        .object({
        kind: protocolIdSchema,
        uri: uriSchema,
        digest: sha256DigestSchema,
    })
        .strict(),
    createdAt: protocolTimestampSchema,
    updatedAt: protocolTimestampSchema,
})
    .strict();
export const currentUniversalWorkItemSchema = universalWorkItemSchema
    .extend({ completionIntent: completionIntentSchema })
    .strict();
export function isCurrentUniversalWorkItem(work) {
    return currentUniversalWorkItemSchema.safeParse(work).success;
}
export function assertCurrentUniversalWorkItem(input) {
    const work = universalWorkItemSchema.parse(input);
    parseRequiredCompletionIntent(work.completionIntent);
    return currentUniversalWorkItemSchema.parse(work);
}
const WORK_TRANSITIONS = {
    REQUESTED: ["CONTRACT_PENDING", "OPEN", "CLOSED"],
    CONTRACT_PENDING: ["OPEN", "CLOSED"],
    OPEN: ["IN_PROGRESS", "CLOSED"],
    IN_PROGRESS: ["SUBMITTED", "CLOSED"],
    SUBMITTED: ["VERIFYING", "IN_PROGRESS", "CLOSED"],
    VERIFYING: ["DETERMINED", "REVIEWING", "IN_PROGRESS"],
    DETERMINED: ["ENFORCED", "REVIEWING", "IN_PROGRESS", "CLOSED"],
    ENFORCED: ["REVIEWING", "CLOSED"],
    REVIEWING: ["VERIFYING", "IN_PROGRESS", "CLOSED"],
    CLOSED: [],
};
export function canTransitionUniversalWorkItem(from, to) {
    return from === to || WORK_TRANSITIONS[from].includes(to);
}
export function transitionUniversalWorkItem(input) {
    const work = assertCurrentUniversalWorkItem(input.work);
    if (!canTransitionUniversalWorkItem(work.state, input.to)) {
        throw new Error(`illegal work transition ${work.state} -> ${input.to}`);
    }
    if (Date.parse(input.at) < Date.parse(work.updatedAt)) {
        throw new Error("work transition time cannot move backwards");
    }
    return currentUniversalWorkItemSchema.parse({
        ...work,
        state: input.to,
        updatedAt: input.at,
    });
}
export function createUniversalWorkItem(input) {
    return currentUniversalWorkItemSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        id: input.id,
        tenantId: input.tenantId,
        projectId: input.projectId ?? null,
        workType: input.workType,
        title: input.title,
        description: input.description,
        completionIntent: input.completionIntent,
        state: "REQUESTED",
        consequence: input.consequence,
        requestedBy: actorIdentity(input.requester),
        source: input.source,
        createdAt: input.now,
        updatedAt: input.now,
    });
}
